import { ReactNode } from 'react';
import { cx } from './cx';
import { Text } from './Text';
import { Badge } from './Badge';

export interface CardProps {
  /** Card heading; omit for a plain surface. */
  title?: ReactNode;
  description?: ReactNode;
  /** Small count shown as a brand badge next to the title. */
  count?: number;
  /** Right-aligned header controls (search, refresh, actions). */
  actions?: ReactNode;
  /** Removes body padding — for tables/lists that manage their own. */
  flush?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * The surface container — a bordered `surface` card with the Untitled UI
 * elevation, an optional header (title + count badge + description + actions)
 * and a body. The base for catalog cards, list cards and panels.
 */
export const Card = ({
  title,
  description,
  count,
  actions,
  flush,
  className,
  children,
}: CardProps) => (
  <section
    className={cx(
      'overflow-hidden rounded-xl border border-default bg-surface shadow-xs',
      className,
    )}
  >
    {(title || actions) && (
      <header className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4 pb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {title && (
              <Text variant="title" className="text-base">
                {title}
              </Text>
            )}
            {typeof count === 'number' && <Badge color="brand">{count}</Badge>}
          </div>
          {description && (
            <Text variant="body" tone="tertiary" className="mt-0.5">
              {description}
            </Text>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </header>
    )}
    <div className={flush ? '' : 'px-5 pt-1 pb-5'}>{children}</div>
  </section>
);
